import { useQuery } from "react-query";
import { MigrationSetWithImports, useSmokeballApp } from "../shared";
import log from "../shared/utils/log";
import { requestHelper, ServerError, UnauthorizedError } from "../shared/utils";
import { useState } from "react";

const fetchMigration = async ({
  accountId,
  migrationSetId,
  token,
}: {
  accountId: string;
  migrationSetId: number;
  token?: () => Promise<string>;
}): Promise<MigrationSetWithImports> => {
  const endpoint = `/${migrationSetId}`;
  const data = await requestHelper(
    accountId,
    endpoint,
    "Failed to retrieve import.",
    "GET",
    token,
  );
  log("Fetched migration set: ", data);

  return data.migrationSet;
};

export const useMigration = ({
  accountId,
  migrationSetId,
  token,
}: {
  accountId: string;
  migrationSetId: number;
  token?: () => Promise<string>;
}) => {
  const { context } = useSmokeballApp();
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [isServerError, setIsServerError] = useState(false);

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["migration", accountId, migrationSetId],
    queryFn: async () => {
      try {
        const result = await fetchMigration({
          accountId,
          migrationSetId,
          token,
        });
        setIsUnauthorized(false); // Reset on success
        setIsServerError(false); // Reset on success
        return result;
      } catch (error) {
        if (error instanceof UnauthorizedError) {
          // Stop retrying on UnauthorizedError
          setIsUnauthorized(true);
          setIsServerError(false);
        }
        // Stop retrying on ServerError
        if (error instanceof ServerError) {
          setIsServerError(true);
          setIsUnauthorized(false);
        }
        throw error;
      }
    },
    refetchInterval: 5000,
    // Do not run the query until accountId is valid
    enabled: !!accountId,
    retry: (failureCount, error) => {
      // Stop retrying on UnauthorizedError or ServerError
      return !(error instanceof UnauthorizedError || error instanceof ServerError) 
    },
  });

  const currentUser = context?.userId;
  const blockingUserName = data ? getBlockingUser(data, currentUser) : null;

  // Check if any import is in progress by another user
  const disableAccessToSecondaryUser = blockingUserName !== null;
  const disableAccessAsPrimaryUserCancelled = data?.status === "Cancelled";
  const disableAccessAsPrimaryUserSubmitted =
    data?.status === "Submitted" ||
    data?.status === "Loading" ||
    data?.status === "Complete";

  return {
    data,
    refetch,
    isLoading,
    isUnauthorized,
    isServerError,
    blockingUserName,
    disableAccessToSecondaryUser,
    disableAccessAsPrimaryUserCancelled,
    disableAccessAsPrimaryUserSubmitted,
  };
};

const getBlockingUser = (
  data: MigrationSetWithImports | undefined,
  currentUser: string | undefined,
) => {
  const importBlocking = data?.imports.find(
    (importData) =>
      (importData.uploadInProgress as unknown as number) === 1 &&
      importData.lastUpdatedBy !== currentUser,
  );
  return importBlocking ? importBlocking.lastUpdatedByName : null;
};
